import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Gamepad2, Plus, Trash2, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import AddActivityDialog from "@/components/dialogs/AddActivityDialog";

interface LessonActivitiesManagerProps {
  lessonId: string;
  nurseryId: string;
}

const ACTIVITY_TYPE_LABELS: Record<string, string> = {
  multiple_choice: "اختيار من متعدد",
  drag_drop: "سحب وإفلات",
  ordering: "ترتيب",
  fill_blank: "أكمل الفراغ",
};

const ACTIVITY_TYPE_EMOJIS: Record<string, string> = {
  multiple_choice: "📝",
  drag_drop: "🎯",
  ordering: "🔢",
  fill_blank: "✏️",
};

const LessonActivitiesManager = ({ lessonId, nurseryId }: LessonActivitiesManagerProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);

  const { data: activities, isLoading } = useQuery({
    queryKey: ["lesson-activities", lessonId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("lesson_activities")
        .select("*")
        .eq("lesson_id", lessonId)
        .order("sort_order");
      if (error) throw error;
      return data || [];
    },
  });

  // Fetch response counts
  const { data: responseCounts } = useQuery({
    queryKey: ["activity-response-counts", lessonId],
    queryFn: async () => {
      if (!activities?.length) return {};
      const counts: Record<string, number> = {};
      for (const act of activities) {
        const { count } = await supabase
          .from("activity_responses")
          .select("*", { count: "exact", head: true })
          .eq("activity_id", act.id);
        counts[act.id] = count || 0;
      }
      return counts;
    },
    enabled: !!activities?.length,
  });

  const handleDelete = async (activityId: string) => {
    const { error } = await supabase.from("lesson_activities").delete().eq("id", activityId);
    if (error) {
      toast({ title: "خطأ في الحذف", variant: "destructive" });
    } else {
      toast({ title: "تم حذف النشاط" });
      queryClient.invalidateQueries({ queryKey: ["lesson-activities", lessonId] });
    }
  };

  return (
    <>
      <Card className="shadow-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Gamepad2 className="w-4 h-4 text-primary" />
              الأنشطة التفاعلية ({activities?.length || 0})
            </CardTitle>
            <Button size="sm" onClick={() => setShowAdd(true)} className="gap-1">
              <Plus className="w-4 h-4" />
              إضافة نشاط
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-primary" />
            </div>
          ) : !activities?.length ? (
            <div className="py-8 text-center">
              <Gamepad2 className="w-10 h-10 text-muted-foreground/20 mx-auto mb-2" />
              <p className="text-muted-foreground text-sm">لا توجد أنشطة بعد</p>
              <p className="text-xs text-muted-foreground mt-1">أضف أنشطة تفاعلية مثل اختيار من متعدد وسحب وإفلات</p>
            </div>
          ) : (
            <div className="space-y-2">
              {activities.map((act, i) => (
                <div key={act.id} className="flex items-center gap-3 p-3 rounded-lg border bg-muted/10 hover:bg-muted/30 transition-colors">
                  <span className="text-xl">{ACTIVITY_TYPE_EMOJIS[act.activity_type] || "📋"}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-foreground truncate">{act.title}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <Badge variant="outline" className="text-[10px]">
                        {ACTIVITY_TYPE_LABELS[act.activity_type] || act.activity_type}
                      </Badge>
                      <span className="text-[10px] text-muted-foreground">{act.max_score} نقطة</span>
                      {responseCounts?.[act.id] ? (
                        <span className="text-[10px] text-primary">{responseCounts[act.id]} إجابة</span>
                      ) : null}
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete(act.id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <AddActivityDialog
        open={showAdd}
        onOpenChange={setShowAdd}
        lessonId={lessonId}
        nurseryId={nurseryId}
        sortOrder={activities?.length || 0}
      />
    </>
  );
};

export default LessonActivitiesManager;
